import { useEffect, useState } from 'react';
import { useLocalParticipant } from '@livekit/components-react';
import {
  applyRNNoiseToTrack,
  stopRNNoiseProcessing,
} from '../../helpers/rnnoise-processor';

/**
 * Component that automatically applies RNNoise to the local participant's microphone
 * when enabled through application settings
 */
function RNNoiseProvider({ children, autoEnable = false }) {
  const { localParticipant } = useLocalParticipant();
  const [isEnabled, setIsEnabled] = useState(autoEnable);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load RNNoise settings from localStorage
  useEffect(() => {
    const savedSetting = localStorage.getItem('rnnoiseEnabled');
    if (savedSetting !== null) {
      setIsEnabled(savedSetting === 'true');
    }
    setIsInitialized(true);
  }, []);

  // Save RNNoise settings to localStorage when changed
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem('rnnoiseEnabled', isEnabled.toString());
    }
  }, [isEnabled, isInitialized]);

  // Apply RNNoise to the local participant's microphone when enabled
  useEffect(() => {
    if (!localParticipant) return;

    const applyNoiseReduction = async () => {
      try {
        // Find the microphone audio track
        const audioTracks = Array.from(
          localParticipant.audioTrackPublications.values()
        );
        const micTrack = audioTracks.find(
          (pub) => pub.track && !pub.track.isMuted
        );

        if (micTrack && micTrack.track && isEnabled) {
          console.log('Applying RNNoise to local microphone');
          await applyRNNoiseToTrack(micTrack.track);
        } else if (
          micTrack &&
          micTrack.track &&
          !isEnabled &&
          micTrack.track._rnnoiseProcessing
        ) {
          // If RNNoise was previously enabled but is now disabled, stop processing
          console.log('Stopping RNNoise on local microphone');
          await stopRNNoiseProcessing(micTrack.track);
        }
      } catch (error) {
        console.error('Error managing RNNoise:', error);
      }
    };

    // Apply or remove noise reduction when isEnabled changes
    applyNoiseReduction();

    // Listen for new track publications
    const handleTrackPublished = async (publication) => {
      if (
        publication.kind === 'audio' &&
        publication.track &&
        !publication.track.isMuted &&
        isEnabled
      ) {
        await applyRNNoiseToTrack(publication.track);
      }
    };

    // Listen for track unmuted events
    const handleTrackUnmuted = async (track) => {
      if (track.kind === 'audio' && isEnabled) {
        await applyRNNoiseToTrack(track);
      }
    };

    localParticipant.on('trackPublished', handleTrackPublished);
    localParticipant.on('trackUnmuted', handleTrackUnmuted);

    return () => {
      localParticipant.off('trackPublished', handleTrackPublished);
      localParticipant.off('trackUnmuted', handleTrackUnmuted);
    };
  }, [localParticipant, isEnabled]);

  return children;
}

export default RNNoiseProvider;
